import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Grid,
} from '@mui/material';
import { API_URL } from '../utils/api';

const StatusChangeModal = ({
  isOpen,
  onClose,
  attendanceId,
  currentStatus,
  studentName,
  employeeId,
  onStatusChanged,
  defaultCheckInTime = '07:00',
  defaultCheckOutTime = '13:00',
}) => {
  const [newStatus, setNewStatus] = useState(currentStatus || 'present');
  const [checkInTime, setCheckInTime] = useState(defaultCheckInTime);
  const [checkOutTime, setCheckOutTime] = useState(defaultCheckOutTime);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const statusOptions = [
    { value: 'present', label: 'Hadir' },
    { value: 'late', label: 'Terlambat' },
    { value: 'sick', label: 'Sakit' },
    { value: 'permission', label: 'Izin' },
    { value: 'absent', label: 'Alpa' },
    { value: 'half_day', label: 'Setengah Hari' },
  ];

  useEffect(() => {
    if (isOpen) {
      setNewStatus(currentStatus || 'present');
      setCheckInTime(defaultCheckInTime);
      setCheckOutTime(defaultCheckOutTime);
      setNotes('');
      setError('');
    }
  }, [isOpen, currentStatus, defaultCheckInTime, defaultCheckOutTime]);

  const validateTimes = () => {
    if (!checkInTime || !checkOutTime) {
      setError('Jam masuk dan jam pulang harus diisi');
      return false;
    }

    const [inHour, inMin] = checkInTime.split(':').map(Number);
    const [outHour, outMin] = checkOutTime.split(':').map(Number);
    const inMinutes = inHour * 60 + inMin;
    const outMinutes = outHour * 60 + outMin;

    if (inMinutes >= outMinutes) {
      setError('Jam masuk harus lebih kecil dari jam pulang');
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    try {
      setError('');

      if (!validateTimes()) {
        return;
      }

      setLoading(true);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token autentikasi tidak ditemukan');
      }

      const payload = {
        status: newStatus,
        check_in_time: checkInTime,
        check_out_time: checkOutTime,
        notes: notes || null,
      };

      // If attendance_id exists, add it; otherwise add employee_id for create
      if (attendanceId) {
        payload.attendance_id = attendanceId;
      } else if (employeeId) {
        payload.employee_id = employeeId;
      } else {
        throw new Error('ID siswa/guru tidak valid');
      }

      const response = await fetch(`${API_URL}/api/attendance/create-or-update-manual`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Gagal menyimpan data absensi');
      }

      if (onStatusChanged) {
        onStatusChanged(data.data);
      }

      handleClose();
    } catch (err) {
      console.error('❌ Status change error:', err);
      setError(err.message || 'Terjadi kesalahan');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNewStatus(currentStatus || 'present');
    setCheckInTime(defaultCheckInTime);
    setCheckOutTime(defaultCheckOutTime);
    setNotes('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Ubah/Buat Data Absensi
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
          {studentName}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {currentStatus && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Status saat ini: <strong>{statusOptions.find(s => s.value === currentStatus)?.label || currentStatus}</strong>
            </Typography>
          </Box>
        )}

        <Grid container spacing={2}>
          {/* Status Selection */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={newStatus}
                label="Status"
                onChange={(e) => setNewStatus(e.target.value)}
                disabled={loading}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Check-in Time */}
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Jam Masuk (HH:mm)"
              type="time"
              value={checkInTime}
              onChange={(e) => setCheckInTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
              disabled={loading}
              required
            />
          </Grid>

          {/* Check-out Time */}
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Jam Pulang (HH:mm)"
              type="time"
              value={checkOutTime}
              onChange={(e) => setCheckOutTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
              disabled={loading}
              required
            />
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Catatan (Opsional)"
              placeholder="Masukkan alasan atau catatan"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Batal
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Simpan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatusChangeModal;
